import { AnimationBuilder, AnimationMetadata, animate, style } from "@angular/animations";
import { Renderer2 } from "@angular/core";

export interface ElementOffset {
	top: number;
	left: number;
}

export class DomSmallUtils {

    // Replacement for jQuery(html)
    public static generateElements(html: string): HTMLElement {
        const template = document.createElement('template');
        template.innerHTML = html.trim();
        return <HTMLElement>template.content.children[0];
	}

	public static addClass(r: Renderer2, el: Element | undefined, className: string) {
		if (el) r.addClass(el, className);
	}

	public static removeClass(r: Renderer2, el: Element | undefined, className: string) {
		if (el) r.removeClass(el, className);
	}

	public static setStyle(r: Renderer2, el: Element | undefined, style: string, value: string) {
		if (el) r.setStyle(el, style, value);
	}

	public static removeStyle(r: Renderer2, el: Element | undefined, style: string) {
		if (el) r.removeStyle(el, style);
	}

    // Replacement for jQuery.find(selector)[0]
    public static find(el: Element | undefined, selector: string): HTMLElement | undefined {
        if(!el) return;
        return <HTMLElement>el.querySelector(`:scope ${selector}`) || undefined;
    }

    // Replacement for jQuery.find(selector)
    public static findAll(el: Element | undefined, selector: string): NodeListOf<HTMLElement> | undefined {
        if(!el) return;
        return el.querySelectorAll(`:scope ${selector}`) || undefined;
    }

    // Replacement for jQuery.empty()
    public static empty(el: Node | undefined): void {
        if(!el) return;
        while (el.lastChild) {
            el.removeChild(el.lastChild);
        }
	}

	// Replacement for jQuery.trigger(eventType)
	public static trigger(el: HTMLElement & Record<string, any> | undefined, eventType: any) {
        if(!el) return;
        if (typeof eventType === 'string') {
            const elProp = el[eventType];
            if (typeof elProp === 'function') {
                elProp();
            } else {
                el.dispatchEvent(new Event(eventType, { bubbles: true }));
            }
        } else {
            el.dispatchEvent(eventType);
        }
	}

    // Replacement for jQuery.hasClass(className)
    public static hasClass(el: Element | undefined, className: string): boolean {
        if(!el) return false;
        return el.classList.contains(className);
    }

    public static prev(el?: Element, selector?: string): HTMLElement | undefined {
        if(!el) return;

        const prevEl = <HTMLElement>el.previousElementSibling;
        if (!selector || (prevEl && prevEl.matches(selector))) {
            return prevEl;
        }
    }

    public static next(el?: Element, selector?: string): HTMLElement | undefined {
        if(!el) return;

        const nextEl = <HTMLElement>el.nextElementSibling;
        if (!selector || (nextEl && nextEl.matches(selector))) {
            return nextEl;
        }
    }

    // Replacement for jQuery.position()
    public static position(elem: HTMLElement, definedOffsetParent?: HTMLElement): ElementOffset {
        if (!elem) return { top: 0, left: 0 };
    
        let doc: Document = elem.ownerDocument;
        let offsetParent: HTMLElement = <HTMLElement>elem.offsetParent || doc.documentElement;
        let elementOffset: ElementOffset = elem.getBoundingClientRect();
        let parentOffset: ElementOffset = { top: 0, left: 0 };
    
        if (getComputedStyle(elem).position !== "fixed") {
            elementOffset = DomSmallUtils.offset(elem);
            
            while (offsetParent && (offsetParent === doc.body || offsetParent === doc.documentElement) && getComputedStyle(elem).position === "static") {
                offsetParent = <HTMLElement>offsetParent.parentNode;
            }
            
            if (definedOffsetParent) offsetParent = definedOffsetParent;
    
            if (offsetParent && offsetParent !== elem && offsetParent.nodeType === 1) {
                parentOffset = DomSmallUtils.offset(offsetParent);
                parentOffset.top += (parseFloat(getComputedStyle(offsetParent).borderTopWidth) || 0);
                parentOffset.left += (parseFloat(getComputedStyle(offsetParent).borderLeftWidth) || 0);
            }
        }
    
        return {
            top: elementOffset.top - parentOffset.top - (parseFloat(getComputedStyle(elem).marginTop) || 0),
            left: elementOffset.left - parentOffset.left - (parseFloat(getComputedStyle(elem).marginLeft) || 0)
        };
    }

	// Replacement for jQuery.outerHeight()
	public static outerHeight(el?: HTMLElement): number {
		if (!el) return 0;

		return el.offsetHeight || DomSmallUtils.calcHeight(el);
	}

	// Calculate element height by ClientRect or get it from style
	public static calcHeight(el: HTMLElement): number {
		let result: number = 0;

		//boundlist.component is hidden when it's height calculated 
		const isNotDisplaying = getComputedStyle(el).display == "none";

		if (isNotDisplaying) {
			el.style.display = 'block';
		}

		const rect: DOMRect = el.getBoundingClientRect();
		result = rect.bottom - rect.top;

		// Get height from style
		if (!result) {
			const style = window.getComputedStyle(el, null);
			const height = parseInt(style.getPropertyValue('height'));

			if (!isNaN(height))
				result = height;
		}

		if (isNotDisplaying) {
			el.style.display = 'none';
		}

		return result;
	}

	// Replacement for jQuery.outerWidth()
	public static outerWidth(el?: HTMLElement): number {
		if (!el) return 0;

		return el.offsetWidth || DomSmallUtils.calcWidth(el);
	}

	/**
	 * Replacement for jQuery.outerHeight(includeMargins = true)
	 *
	 */
	public static outerHeightFull(el: HTMLElement): number {
		if (!el) return 0;

		let result: number = 0;

		const style = window.getComputedStyle(el);

		let marginTop = parseInt(style.getPropertyValue('margin-top'));
		marginTop = isNaN(marginTop) ? 0 : marginTop;

		let marginBottom = parseInt(style.getPropertyValue('margin-bottom'));
		marginBottom = isNaN(marginBottom) ? 0 : marginBottom;

		result = el.getBoundingClientRect().height + marginTop + marginBottom;

		return result || DomSmallUtils.calcHeight(el);
	}

	/**
	 * Replacement for jQuery.outerWidth(includeMargins = true)
	 *
	 */
	public static outerWidthFull(el: HTMLElement | Element): number {
		if (!el) return 0;

		let result: number = 0;

		const style = window.getComputedStyle(el);

		let marginLeft = parseInt(style.getPropertyValue('margin-left'));
		marginLeft = isNaN(marginLeft) ? 0 : marginLeft;

		let marginRight = parseInt(style.getPropertyValue('margin-right'));
		marginRight = isNaN(marginRight) ? 0 : marginRight;

		result = el.getBoundingClientRect().width + marginLeft + marginRight;

		return result || DomSmallUtils.calcWidth(el as HTMLElement);
	}

	// Calculate element width by ClientRect or get it from style
	public static calcWidth(el: HTMLElement): number {
		let result: number = 0;

		const isNotDisplaying = getComputedStyle(el).display == "none";

		if (isNotDisplaying) {
			el.style.display = 'block';
		}

		const rect: DOMRect = el.getBoundingClientRect();
		result = rect.right - rect.left;

		// Get width from style
		if (!result) {
			const style = window.getComputedStyle(el);
			const width = parseInt(style.getPropertyValue('width'));

			if (!isNaN(width))
				result = width;
		}

		if (isNotDisplaying) {
			el.style.display = 'none';
		}

		return result;
	}

    // Replacement for jQuery.innerWidth()
    public static innerWidth(el: HTMLElement): number {
        if (!el) return 0;

        return el.clientWidth || DomSmallUtils.calcWidth(el);
    }

    // Replacement for jQuery.innerHeight()
    public static innerHeight(el: HTMLElement): number {
        if (!el) return 0;

        return el.clientHeight || DomSmallUtils.calcHeight(el);
    }

	/**
	 * Replacement for jQuery.width()
	 *
	 */
	public static width(el?: HTMLElement): number {
		if (!el) return 0;

		let result: number = 0;

		const style = window.getComputedStyle(el);

		let paddingLeft = parseInt(style.getPropertyValue('padding-left'));
		paddingLeft = isNaN(paddingLeft) ? 0 : paddingLeft;

		let paddingRight = parseInt(style.getPropertyValue('padding-right'));
		paddingRight = isNaN(paddingRight) ? 0 : paddingRight;

		let borderLeft = parseInt(style.getPropertyValue('border-left-width'));
		borderLeft = isNaN(borderLeft) ? 0 : borderLeft;

		let borderRight = parseInt(style.getPropertyValue('border-right-width'));
		borderRight = isNaN(borderRight) ? 0 : borderRight;

		result = el.getBoundingClientRect().width - (paddingLeft + paddingRight + borderLeft + borderRight);

		return result || DomSmallUtils.calcWidth(el);
	}

	/**
	 * Replacement for jQuery.height()
	 *
	 */
	public static height(el?: HTMLElement): number {
		if (!el) return 0;

		let result: number = 0;

		const style = window.getComputedStyle(el);

		let paddingTop = parseInt(style.getPropertyValue('padding-top'));
		paddingTop = isNaN(paddingTop) ? 0 : paddingTop;

		let paddingBottom = parseInt(style.getPropertyValue('padding-bottom'));
		paddingBottom = isNaN(paddingBottom) ? 0 : paddingBottom;

		let borderTop = parseInt(style.getPropertyValue('border-top-width'));
		borderTop = isNaN(borderTop) ? 0 : borderTop;

		let borderBottom = parseInt(style.getPropertyValue('border-bottom-width'));
		borderBottom = isNaN(borderBottom) ? 0 : borderBottom;

		result = el.getBoundingClientRect().height - (paddingTop + paddingBottom + borderTop + borderBottom);

		return result || DomSmallUtils.calcHeight(el);
	}

	/**
	 * Replacement for jQuery.parents("[selector]")
	 *
	 */
    
	public static parentsByTag(el?: HTMLElement, selector?: string) {
		if (!el) return [];
		return selector ? this.parents(el).filter(x => x.tagName == selector.toUpperCase()) : this.parents(el);
	}

	public static parentsByClass(el?: HTMLElement, selector?: string) {
		if (!el) return [];
		return selector ? this.parents(el).filter(x => x.classList.contains(selector.substr(1))) : this.parents(el);
	}
    
	public static parents(el: Element): HTMLElement[] {
		let elTemp = el;
		const result: HTMLElement[] = [];
		while (elTemp.parentElement) {
			result.push(elTemp.parentElement);
			elTemp = elTemp.parentElement;
		}
		return result;
	}
	
	/**
	 * Replacement for jQuery.offset()
	 *
	 */
	public static offset(el?: Element): ElementOffset {
		if (!el) return { top: 0, left: 0 };

		const rect = el.getBoundingClientRect();

		if (rect.width || rect.height) {
			const doc = el.ownerDocument;
			const win = doc?.defaultView ?? window;
			const docEl = doc?.documentElement ?? window.document.documentElement;

			return {
				top: rect.top + win.pageYOffset - docEl.clientTop,
				left: rect.left + win.pageXOffset - docEl.clientLeft
			};
		}
		return { top: 0, left: 0 };
	}

    public static setOffset(el: HTMLElement | undefined, offset: { top?: number, left?: number }): void {
        if (!el) return;
        
        const position = getComputedStyle(el).position;
        if(position === "static") el.style.position = "relative";

        var curPosition: ElementOffset,
            curTop: number,
            curLeft: number,
            curCSSTop = getComputedStyle(el).top,
            curCSSLeft = getComputedStyle(el).left;

        if((position === "absolute" || position === "fixed") && (curCSSTop + curCSSLeft).indexOf("auto") > -1) {
            curPosition = DomSmallUtils.position(el);
            curTop = curPosition.top;
            curLeft = curPosition.left;
        } else {
            curTop = parseFloat(curCSSTop) || 0;
            curLeft = parseFloat(curCSSLeft) || 0;
        }

        if(offset.top !== undefined) el.style.top = (offset.top - DomSmallUtils.offset(el).top) + curTop + 'px';
        if(offset.left !== undefined) el.style.left = (offset.left - DomSmallUtils.offset(el).left) + curLeft + 'px';
    }
}